import { LoaderCircleIcon, LogOutIcon } from "lucide-react";
import { useCallback, useState } from "react";
import { useNavigate } from "@tanstack/react-router";

import { cmmdEmbedded } from "../../embed/cmmdEmbedMode";
import { logoutPrimarySession } from "../../environments/primary";
import { toastManager } from "../ui/toast";
import { SidebarGroup, SidebarMenu, SidebarMenuButton, SidebarMenuItem } from "../ui/sidebar";

/**
 * Logging out is the last row of the settings sidebar.
 *
 * The settings rail replaces the whole sidebar, account footer included, so
 * without this row the only way out on a settings page is the command palette.
 * It is deliberately below every nav group rather than inside one: it is not a
 * destination, and a session-ending action does not belong beside "Appearance".
 *
 * Hidden when `cmmdEmbedded`: CMMD owns the session; logging out of Forge
 * inside the iframe would strand the user without leaving CMMD.
 */
export function SettingsSidebarLogout() {
  const navigate = useNavigate();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const embedded = cmmdEmbedded();

  const handleLogoutClick = useCallback(async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    try {
      await logoutPrimarySession();
      await navigate({ to: "/login", replace: true, reloadDocument: true });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to logout current session.";
      toastManager.add({ title: "Could not logout", description: message, type: "error" });
    } finally {
      // Re-enabled even on failure: a session that refused to end is one the
      // user needs to be able to try ending again.
      setIsLoggingOut(false);
    }
  }, [isLoggingOut, navigate]);

  if (embedded) {
    return null;
  }

  return (
    <>
      <div className="mx-4 h-px bg-border/70" />
      <SidebarGroup className="px-2 py-2">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              size="sm"
              data-testid="settings-logout-button"
              disabled={isLoggingOut}
              className="gap-2.5 px-2.5 py-2 text-left text-[13px] text-muted-foreground/70 hover:text-foreground/80"
              onClick={() => {
                void handleLogoutClick();
              }}
            >
              {isLoggingOut ? (
                <LoaderCircleIcon className="size-4 shrink-0 animate-spin text-muted-foreground/60" />
              ) : (
                <LogOutIcon className="size-4 shrink-0 text-muted-foreground/60" />
              )}
              <span className="truncate">{isLoggingOut ? "Logging out..." : "Log out"}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarGroup>
    </>
  );
}
